import React, {useCallback, useEffect, useState} from 'react';
import _ from 'lodash';
import block from 'bem-cn-lite';
import {YCSelect} from '@yandex-data-ui/common';
import {useFieldArray} from 'react-final-form-arrays';
import {Field, useForm, useFormState} from 'react-final-form';
import {FormFieldRadioButton, FormFieldSelect} from '@yandex-data-ui/cloud-components';

import {CalculatorProps} from 'components/calculator/models';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';
import {FormFieldNumberInput} from 'components/formFields';
import CalculatorForm from 'components/calculator/CalculatorForm/CalculatorForm';
import ClosableSection from 'components/calculator/ClosableSection/ClosableSection';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import AddButton from 'components/Buttons/AddButton/AddButton';
import {toSelectItems} from 'components/formFields/utils';
import {toSeconds, fromSeconds} from 'utils';
import {I18nKFunction} from 'i18n';

import './SpeechKitForm.scss';

const b = block('SpeechKitForm');
const CHANNELS_DEFAULT = 2;

const getTypeOptions = _.memoize((i18nK) => [
    {content: i18nK('value_speechkit-type-tts'), value: 'tts'},
    {content: i18nK('value_speechkit-type-stt'), value: 'stt'},
]);

const getDurationOptions = _.memoize((i18nK) => [
    {content: i18nK('value_speechkit-duration-short'), value: 'short'},
    {content: i18nK('value_speechkit-duration-long'), value: 'long'},
]);

const getTimeUnitOptions = _.memoize((i18nK) => [
    {content: i18nK('value_time-second'), value: 'second'},
    {content: i18nK('value_time-minute'), value: 'minute'},
    {content: i18nK('value_time-hour'), value: 'hour'},
]);

const getVoiceTypeOptions = _.memoize((i18nK) => [
    {content: i18nK('value_voice-type-premium'), value: 'premium'},
    {content: i18nK('value_voice-type-standard'), value: 'standard'},
]);

interface SpeechKitFormItemValues {
    type: string;
    duration: string;
    channels: number;
    time: number;
    voiceType: string;
    count: number;
}

interface SpeechKitFormValues {
    items: SpeechKitFormItemValues[];
    [key: `${string}.channels`]: number;
    [key: `${string}.time`]: number;
}

interface SpeechKitFormFieldsProps {
    name: string;
    index: number;
    showClose: boolean;
    onClose: () => void;
    i18nK: I18nKFunction;
}

const SpeechKitFormFields: React.FC<SpeechKitFormFieldsProps> = (props) => {
    const {name, index, showClose, onClose, i18nK} = props;

    const timeUnitOptions = getTimeUnitOptions(i18nK);
    const typeOptions = getTypeOptions(i18nK);
    const durationOptions = getDurationOptions(i18nK);
    const voiceTypeOptions = getVoiceTypeOptions(i18nK);

    const {change} = useForm<SpeechKitFormValues>();
    const {
        values: {items},
    } = useFormState<SpeechKitFormValues>();
    const [timeUnit, setTimeUnit] = useState(timeUnitOptions[0].value);

    const {type, duration, channels, time} = items[index];

    return (
        <CalculatorFormLayout.Section key={index}>
            <ClosableSection showClose={showClose} onClose={onClose}>
                <CalculatorFormLayout.Row
                    title={i18nK('field_speechkit-type')}
                    note={i18nK('context_speechkit-type')}
                >
                    <Field name={`${name}.type`}>
                        {(fieldProps) => (
                            <FormFieldSelect
                                {...fieldProps}
                                size="n"
                                items={toSelectItems(typeOptions)}
                                className={b('type')}
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                {type === 'stt' && (
                    <React.Fragment>
                        <CalculatorFormLayout.Row
                            title={i18nK('field_speechkit-duration')}
                            note={i18nK('context_speechkit-duration')}
                        >
                            <Field name={`${name}.duration`}>
                                {(fieldProps) => (
                                    <FormFieldRadioButton
                                        {...fieldProps}
                                        size="l"
                                        options={durationOptions}
                                        controlClassName={b('duration')}
                                        width="max"
                                    />
                                )}
                            </Field>
                        </CalculatorFormLayout.Row>
                        {duration === 'long' && (
                            <CalculatorFormLayout.Row
                                title={i18nK('field_speechkit-channels')}
                                note={i18nK('context_speechkit-channels')}
                            >
                                <Field name={`${name}.channels`}>
                                    {(fieldProps) => (
                                        <FormFieldNumberInput
                                            {...fieldProps}
                                            range={{min: 1, max: 9}}
                                            step={1}
                                            className={b('channels')}
                                            defaultValue={2}
                                            maxLength={8}
                                            onBlur={(event: FocusEvent) => {
                                                if (!channels) {
                                                    change(
                                                        `${name}.channels`,
                                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                        CHANNELS_DEFAULT as unknown as any,
                                                    );
                                                    event.preventDefault();
                                                }
                                            }}
                                        />
                                    )}
                                </Field>
                            </CalculatorFormLayout.Row>
                        )}
                    </React.Fragment>
                )}
                {type === 'stt' && (
                    <CalculatorFormLayout.Row
                        title={i18nK('field_speechkit-time')}
                        note={i18nK('context_speechkit-time', {
                            type:
                                duration === 'long'
                                    ? i18nK('context_speechkit-time-long')
                                    : i18nK('context_speechkit-time-short'),
                        })}
                    >
                        <div className={b('time')}>
                            <Field
                                name={`${name}.time`}
                                format={(value) => String(fromSeconds(value, timeUnit))}
                                parse={(value) => String(toSeconds(value, timeUnit))}
                            >
                                {(fieldProps) => (
                                    <FormFieldNumberInput
                                        {...fieldProps}
                                        range={{min: 1}}
                                        className={b('time-input')}
                                        defaultValue={1}
                                        maxLength={8}
                                        withoutControls={true}
                                    />
                                )}
                            </Field>
                            <YCSelect
                                size="n"
                                showSearch={false}
                                className={b('time-select')}
                                value={timeUnit}
                                onUpdate={(newTimeUnit) => {
                                    change(
                                        `${name}.time`,
                                        toSeconds(
                                            fromSeconds(time, timeUnit),
                                            newTimeUnit,
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        ) as unknown as any,
                                    );

                                    setTimeUnit(newTimeUnit);
                                }}
                                items={toSelectItems(timeUnitOptions)}
                            />
                        </div>
                    </CalculatorFormLayout.Row>
                )}
                {type === 'tts' && (
                    <div className={b('tts')}>
                        <CalculatorFormLayout.Row
                            title={i18nK('field_speechkit-voice-type')}
                            note={i18nK('context_speechkit-voice-type')}
                        >
                            <Field name={`${name}.voiceType`}>
                                {(fieldProps) => (
                                    <FormFieldSelect
                                        {...fieldProps}
                                        size="n"
                                        items={toSelectItems(voiceTypeOptions)}
                                    />
                                )}
                            </Field>
                        </CalculatorFormLayout.Row>
                        <CalculatorFormLayout.Row
                            title={i18nK('field_speechkit-count')}
                            note={i18nK('context_speechkit-count')}
                        >
                            <Field name={`${name}.count`}>
                                {(fieldProps) => (
                                    <FormFieldNumberInput
                                        {...fieldProps}
                                        range={{min: 1}}
                                        step={10000}
                                        className={b('count')}
                                        defaultValue={1}
                                    />
                                )}
                            </Field>
                        </CalculatorFormLayout.Row>
                    </div>
                )}
            </ClosableSection>
        </CalculatorFormLayout.Section>
    );
};

type SpeechKitFormProps = CalculatorProps & WithTranslationProps;

const SpeechKitForm: React.FC<SpeechKitFormProps> = (props) => {
    const {i18nK, title} = props;
    const {fields} = useFieldArray('items');
    const {initialize} = useForm<SpeechKitFormValues>();
    const {initialValues} = useFormState<SpeechKitFormValues>();

    const createItemDefaultData = useCallback((): SpeechKitFormItemValues => {
        const typeOptions = getTypeOptions(i18nK);
        const timeUnitOptions = getTimeUnitOptions(i18nK);
        const durationOptions = getDurationOptions(i18nK);
        const voiceTypeOptions = getVoiceTypeOptions(i18nK);

        return {
            type: typeOptions[0].value,
            duration: durationOptions[0].value,
            time: toSeconds(15, timeUnitOptions[0].value),
            count: 10000,
            voiceType: voiceTypeOptions[0].value,
            channels: CHANNELS_DEFAULT,
        };
    }, [i18nK]);

    useEffect(() => {
        initialize({
            items: [createItemDefaultData()],
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!initialValues) {
        return null;
    }

    return (
        <CalculatorForm title={title} className={b()}>
            <CalculatorFormLayout>
                {fields.map((item, index) => (
                    <SpeechKitFormFields
                        key={index}
                        i18nK={i18nK}
                        index={index}
                        name={item}
                        showClose={Number(fields.length) > 1}
                        onClose={() => fields.remove(index)}
                    />
                ))}
                <CalculatorFormLayout.Section>
                    <AddButton onClick={() => fields.push(createItemDefaultData())}>
                        {i18nK('button_add-api')}
                    </AddButton>
                </CalculatorFormLayout.Section>
            </CalculatorFormLayout>
        </CalculatorForm>
    );
};

export default withTranslation('calculator')(SpeechKitForm);
